import { FCX } from '@models';
import { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import cn from 'classnames';
import { IoFilter } from 'react-icons/io5';
import { MdCheck } from 'react-icons/md';
import { toggleInArray } from '@core/utils/array';
import Popup from '@components/Popup';
import './CategoryFilterHead.scss';

const CategoryFilterHead: FCX<{
    title: string;
    options: string[];
    value: string[];
    onChange: (value: string[]) => void;
    isDisabled?: boolean;
}> = ({
    title,
    options,
    value,
    onChange,
    isDisabled = false,
}) => {
    const triggerRef = useRef<HTMLDivElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [isOpened, setIsOpened] = useState(false);
    const [position, setPosition] = useState<CSSProperties>({});

    const hasActiveFilter = value.length > 0;

    const close = useCallback(() => setIsOpened(false), []);

    const toggleOpen = useCallback(
        (event: React.MouseEvent) => {
            event.stopPropagation();
            if (isDisabled) return;

            const rect = triggerRef.current?.getBoundingClientRect();
            if (rect) {
                setPosition({
                    position: 'fixed',
                    top: rect.bottom + 4,
                    right: Math.max(8, window.innerWidth - rect.right),
                });
            }
            setIsOpened(prev => !prev);
        },
        [isDisabled]
    );

    // Close the dropdown if filtering becomes unavailable (e.g. results reloading).
    useEffect(() => {
        if (isDisabled) setIsOpened(false);
    }, [isDisabled]);

    // Outside-click: the dropdown is portaled, so check both the trigger and the dropdown.
    useEffect(() => {
        if (!isOpened) return;

        const handleClick = (event: MouseEvent) => {
            const target = event.target as Node;
            if (
                !triggerRef.current?.contains(target) &&
                !dropdownRef.current?.contains(target)
            ) {
                close();
            }
        };

        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [isOpened, close]);

    const selectAll = useCallback(
        () => onChange([...options]),
        [onChange, options]
    );

    const deselectAll = useCallback(
        () => onChange([]),
        [onChange]
    );

    const Icon = IoFilter;

    return (
        <div
            ref={triggerRef}
            className={cn(
                'CategoryFilterHead',
                isDisabled && 'is-disabled',
                hasActiveFilter && 'has-active-filter',
            )}
        >
            <span className="CategoryFilterHead__title">{title}</span>
            <button
                type="button"
                className={cn('CategoryFilterHead__icon', isOpened && 'is-opened')}
                onClick={toggleOpen}
                disabled={isDisabled}
                aria-label="Filter by category"
            >
                <Icon style={{ width: 16, height: 16 }}/>
                {hasActiveFilter && (
                    <span className="CategoryFilterHead__badge">{value.length}</span>
                )}
            </button>

            {isOpened && (
                <Popup hasWrapper={false}>
                    <div
                        ref={dropdownRef}
                        className="CategoryFilterHead__dropdown"
                        style={position}
                        onClick={(event) => event.stopPropagation()}
                    >
                        <div className="CategoryFilterHead__dropdown-actions">
                            <button type="button" onClick={selectAll}>select all</button>
                            <span>|</span>
                            <button type="button" onClick={deselectAll}>deselect all</button>
                        </div>
                        <div className="CategoryFilterHead__options">
                            {options.length === 0 ? (
                                <div className="CategoryFilterHead__option is-empty">
                                    No categories
                                </div>
                            ) : (
                                options.map(option => (
                                    <div
                                        key={option}
                                        className={cn(
                                            'CategoryFilterHead__option',
                                            value.includes(option) && 'is-active',
                                        )}
                                        onClick={() => onChange(toggleInArray(value, option))}
                                    >
                                        <span className="CategoryFilterHead__checkbox">
                                            <MdCheck/>
                                        </span>
                                        <span className="CategoryFilterHead__option-title">{option}</span>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </Popup>
            )}
        </div>
    );
};

export default CategoryFilterHead;
